/*******************************************************************************
 * Copyright     : Copyright (c)  2024 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc.No part may be reproduced or transmitted in any form by any
 *                 means or for anypurpose without the express written permission of Honeywell. *
 *******************************************************************************/
import React, { Fragment, useEffect, useState } from "react";
import { Button, Modal, Checkbox, Accordion, AccordionItem } from "@forge/common";
import "./FilterModal.scss";
import { DeviceLinkedStatus, LOCALE_CONST } from "../../../utils/constants";
import { LinkedDeviceListProps } from "../../../models/linkedDeviceList";
import { getDynamicHeaderNames } from "../../../utils/utility";
import { useTranslation } from "react-i18next";

interface FilterModalProps {
  closeModal: (value: boolean) => void;
  onApplyFilters: (value: any) => void;
  clearFilters: () => void;
  filterMetaData: any;
  data: LinkedDeviceListProps[];
  gatewayType: string;
  flags?: any;
}

interface OptionsProps {
  label: string;
  value: string;
}

const FilterModal = (props: FilterModalProps) => {
  const { t } = useTranslation();

  const [selectedFilters, setSelectedFilters] = useState<any>({});
  const [isFilterInitialized, setIsFilterInitialized] = useState(false);
  const [filterOptions, setFilterOptions] = useState<{ [key: string]: string[] }>({});
  const [statusOptions, setStatusOptions] = useState<OptionsProps[]>([]);
  const [headerStrings, setHeaderStrings] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    const tempFilterOptions: { [key: string]: string[] } = {};
    const tempStatusOptions: string[] = [];

    props.data.forEach((item: LinkedDeviceListProps) => {
      Object.keys(item).forEach((key) => {
        if (key !== "status" && item[key] && !tempFilterOptions[key]?.includes(item[key])) {
          tempFilterOptions[key] = tempFilterOptions[key] || [];
          tempFilterOptions[key].push(item[key]);
        }
      });

      if (item.status && !tempStatusOptions.includes(item.status)) {
        tempStatusOptions.push(item.status);
      }
    });

    setStatusOptions(tempStatusOptions.map((status: string) => ({
      label: DeviceLinkedStatus[status],
      value: status
    })));

    Object.keys(tempFilterOptions).forEach((key) => {
      tempFilterOptions[key].sort();
    });

    const requiredCategories = ["type", "model", "manufacturer"];
    const filteredOptions = Object.keys(tempFilterOptions)
      .filter(key => requiredCategories.includes(key))
      .reduce((obj, key) => {
        obj[key] = tempFilterOptions[key];
        return obj;
      }, {} as { [key: string]: string[] });

    setFilterOptions(filteredOptions);
  }, [props.data]);

  useEffect(() => {
    setSelectedFilters(props.filterMetaData || {});
    setIsFilterInitialized(areInitallyFiltersSelected());
  }, [props.filterMetaData]);

  useEffect(() => {
    const headers = getDynamicHeaderNames(props.gatewayType,props.flags) || {};
    
    setHeaderStrings(headers);
  }, [props.gatewayType]);

  const handleCheckboxChange = (category: string, value: string) => {
    setSelectedFilters((prevFilters: any) => {
      const updatedCategory = prevFilters[category] || [];
      return {
        ...prevFilters,
        [category]: updatedCategory.includes(value)
          ? updatedCategory.filter((item: string) => item !== value) // Remove if already selected
          : [...updatedCategory, value] // Add if not selected
      };
    });
  };

  const handleApplyFilters = () => {
    props.onApplyFilters(selectedFilters);
    props.closeModal(false);
  };

  const handleClearFilters = () => {
    setSelectedFilters({});
    props.clearFilters();
    props.onApplyFilters({});
    props.closeModal(false);
  };

  const areFiltersSelected = () => {
    return Object.keys(selectedFilters).some((key) => selectedFilters[key]?.length > 0);
  };

  const areInitallyFiltersSelected = () => {
    return Object.keys(props.filterMetaData || {}).some((key) => props.filterMetaData[key]?.length > 0);
  };

  const isApplyBtnDisabled = () => {
    if (isFilterInitialized) {
      return false;
    } else if (!areFiltersSelected()) {
      return true;
    } else {
      return false;
    }
  };

  const filterButtonRight = (
    <div className="flex gap-2">
      <Button
        variant="secondary"
        className="cancel-button"
        onClick={() => props.closeModal(false)}
      >
        {t(LOCALE_CONST.cancel)}
      </Button>
      <Button variant="primary" onClick={handleApplyFilters} disabled={isApplyBtnDisabled()}>
        {t(LOCALE_CONST.apply)}
      </Button>
    </div>
  );

  const filterButtonLeft = (
    <Button
      variant="quiet-primary"
      onClick={handleClearFilters}
      disabled={!areFiltersSelected()}
    >
      {t(LOCALE_CONST.clearFilters)}
    </Button>
  );

  return (
    <Fragment>
      <Modal
        data-testid="Modal"
        className="filter-modal-dialog"
        onModalClose={() => props.closeModal(false)}
        title={t(LOCALE_CONST.filter)}
        footerRight={filterButtonRight}
        footerLeft={filterButtonLeft}
      >
        <Accordion
          className="filter-accordian"
          defaultOpenIndex={[0]}
          id="sample"
        >
          <AccordionItem id="status" title={t(LOCALE_CONST.status)} titleCounter={selectedFilters?.status?.length || 0}>
            {statusOptions.map((option) => (
              <div key={option.value} className="pb-4">
                <Checkbox
                  id={option.value}
                  checked={selectedFilters.status?.includes(option.value)}
                  onChange={() => handleCheckboxChange("status", option.value)}
                >
                  {t(LOCALE_CONST[option.label.toLowerCase()])}
                </Checkbox>
              </div>
            ))}
          </AccordionItem>
          
          {Object.keys(filterOptions).map((category) => (
            
            <AccordionItem key={category} id={category} title={t(LOCALE_CONST[headerStrings[category] || category])} titleCounter={selectedFilters?.[category]?.length || 0}>
              {filterOptions[category].map((type) => (
                <div key={type} className="pb-4">
                  <Checkbox
                    id={type}
                    checked={selectedFilters[category]?.includes(type)}
                    onChange={() => handleCheckboxChange(category, type)}
                  >
                    {type}
                  </Checkbox>
                </div>
              ))}
            </AccordionItem>
          ))}
        </Accordion>
      </Modal>
    </Fragment>
  );
};

export default FilterModal;